import { useNavigate } from 'react-router-dom'
import { useQuietMode } from '../components/QuietModeProvider'
import NaturalBackground from '../components/NaturalBackground'
import MindPolishLogo from '../components/MindPolishLogo'

const Tools = () => {
  const navigate = useNavigate()
  const { isQuietMode, isLowStimMode } = useQuietMode()

  const tools = [
    {
      title: 'Routine Builder',
      description: 'Build gentle daily habits with AI support',
      emoji: '🌱',
      route: '/routines',
      bgColor: 'bg-mint-50/50',
      hoverColor: 'hover:bg-mint-100/50',
      textColor: 'text-mint-600',
      borderColor: 'border-mint-100/40',
      hoverBorderColor: 'hover:border-mint-200/50',
      focusRing: 'focus:ring-mint-100/50'
    },
    {
      title: 'Breathing Space',
      description: 'Gentle rhythms to help you find your calm',
      emoji: '🌸',
      route: '/breathing',
      bgColor: 'bg-sage-50/50',
      hoverColor: 'hover:bg-sage-100/50',
      textColor: 'text-sage-600',
      borderColor: 'border-sage-100/40',
      hoverBorderColor: 'hover:border-sage-200/50',
      focusRing: 'focus:ring-sage-100/50'
    },
    {
      title: 'Gratitude Journal',
      description: 'A quiet place for your gentle thoughts',
      emoji: '🌾',
      route: '/journal',
      bgColor: 'bg-peach-50/50',
      hoverColor: 'hover:bg-peach-100/50',
      textColor: 'text-peach-600',
      borderColor: 'border-peach-100/40',
      hoverBorderColor: 'hover:border-peach-200/50',
      focusRing: 'focus:ring-peach-100/50'
    },
    {
      title: 'Mood Check-In',
      description: 'Notice how you feel, softly and without judgment',
      emoji: '🌙',
      route: '/mood',
      bgColor: 'bg-neutral-50/50',
      hoverColor: 'hover:bg-neutral-100/50',
      textColor: 'text-neutral-600',
      borderColor: 'border-neutral-100/40',
      hoverBorderColor: 'hover:border-neutral-200/50',
      focusRing: 'focus:ring-neutral-100/50'
    }
  ]

  return (
    <NaturalBackground variant="meadow">
      <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className={`flex items-center justify-between ${isLowStimMode ? 'mb-12 sm:mb-16' : 'mb-8 sm:mb-12'}`}>
          <button
            onClick={() => navigate('/home')}
            className={`rounded-2xl bg-sage-50/60 hover:bg-sage-100/60 border border-sage-100/40 hover:border-sage-200/50
                       transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 ${isLowStimMode ? 'p-4 sm:p-5' : 'p-3 sm:p-4'}`}
          >
            <svg className={`text-sage-500 dark:text-sage-300 ${isLowStimMode ? 'w-6 h-6' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <MindPolishLogo size="sm" />
          
          <div className={`${isLowStimMode ? 'w-14 sm:w-16' : 'w-11 sm:w-13'}`}></div>
        </div>

        {/* Welcome Section */}
        {!isQuietMode && (
          <div className={`text-center ${isLowStimMode ? 'mb-12 sm:mb-16' : 'mb-8 sm:mb-12'}`}>
            <h2 className={`text-sage-600 dark:text-sage-300 font-light mb-2 ${isLowStimMode ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg'}`}>
              Choose what feels soft today
            </h2>
            <p className={`text-sage-500 dark:text-sage-400 font-light leading-relaxed max-w-sm mx-auto ${isLowStimMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
              Each tool is here when you need it
            </p>
          </div>
        )}

        {/* Tools Grid */}
        <div className={`max-w-sm mx-auto ${isLowStimMode ? 'space-y-8 sm:space-y-10' : 'space-y-4 sm:space-y-6'}`}>
          {tools.map((tool, index) => (
            <button
              key={index}
              onClick={() => navigate(tool.route)}
              className={`w-full ${tool.bgColor} ${tool.hoverColor} rounded-3xl 
                         border ${tool.borderColor} ${tool.hoverBorderColor}
                         transition-all duration-700 ease-out
                         focus:outline-none focus:ring-4 ${tool.focusRing} focus:ring-offset-2 focus:ring-offset-transparent
                         shadow-sm hover:shadow-md ${isLowStimMode ? 'p-8 sm:p-10' : 'p-6 sm:p-8'}`}
            >
              <div className={`flex items-center ${isLowStimMode ? 'space-x-5 sm:space-x-6' : 'space-x-4 sm:space-x-5'}`}>
                <div className={`${isLowStimMode ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}>
                  {tool.emoji}
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`${tool.textColor} dark:${tool.textColor} font-light mb-1 ${isLowStimMode ? 'text-lg sm:text-xl' : 'text-sm sm:text-base'}`}>
                    {tool.title}
                  </h3>
                  {!isQuietMode && (
                    <p className={`${tool.textColor} dark:${tool.textColor} opacity-70 font-light leading-relaxed ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
                      {tool.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Gentle Footer */}
        {!isQuietMode && (
          <div className={`text-center ${isLowStimMode ? 'mt-16 sm:mt-20' : 'mt-12 sm:mt-16'}`}>
            <p className={`text-sage-400 dark:text-sage-500 font-light leading-relaxed max-w-xs mx-auto mb-2 ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
              Try one, try all, or just rest here a moment
            </p>
            <p className={`text-sage-400 dark:text-sage-500 font-light leading-relaxed max-w-xs mx-auto ${isLowStimMode ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>
              You're doing beautifully by being here
            </p>
          </div>
        )}
      </div>
    </NaturalBackground>
  )
}

export default Tools